import React, { useState, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { strukturOrganisasiService } from '../../services/strukturOrganisasiService';

const StrukturOrganisasi = () => {
    const [zoom, setZoom] = useState(1);
    const [activeSlide, setActiveSlide] = useState(0);
    const [slides, setSlides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        const result = await strukturOrganisasiService.getPublicStruktur();
        if (result.success) {
            setSlides(result.data?.data || result.data || []);
        } else {
            setError(result.message);
        }
        setLoading(false);
    };

    const nextSlide = () => {
        if (slides.length > 0) {
            setActiveSlide((prev) => (prev + 1) % slides.length);
            setZoom(1);
        }
    };

    const prevSlide = () => {
        if (slides.length > 0) {
            setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
            setZoom(1);
        }
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return path;
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-12 text-center">
                <FaSpinner className="animate-spin text-4xl text-[#2E7D32] mx-auto mb-4" />
                <p className="text-gray-500">Memuat data...</p>
            </div>
        );
    }

    if (error || slides.length === 0) {
        return (
            <div className="container mx-auto px-4 py-12">
                <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A] mb-2">
                    Struktur Organisasi
                </h1>
                <p className="text-gray-600 mb-6">
                    Badan Pengelola Keuangan dan Aset Daerah Kota Magelang
                </p>
                <div className="card-glass text-center py-12">
                    <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-700 mb-2">Data Belum Tersedia</h2>
                    <p className="text-gray-500">
                        {error || 'Struktur organisasi belum diinput oleh admin'}
                    </p>
                </div>
            </div>
        );
    }

    const currentSlide = slides[activeSlide];

    return (
        <div className="container mx-auto px-4 py-12">
            <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A] mb-2">
                Struktur Organisasi
            </h1>
            <p className="text-gray-600 mb-6">
                Badan Pengelola Keuangan dan Aset Daerah Kota Magelang
            </p>

            {/* Kontrol zoom */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">
                <button
                    onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
                    className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                >
                    −
                </button>
                <span className="text-sm font-medium">{Math.round(zoom * 100)}%</span>
                <button
                    onClick={() => setZoom(Math.min(2, zoom + 0.1))}
                    className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                >
                    +
                </button>
                <button
                    onClick={() => setZoom(1)}
                    className="px-3 py-1 bg-[#2E7D32] text-white rounded hover:bg-[#4CAF50] text-sm"
                >
                    Reset
                </button>
            </div>

            {/* Slide dengan navigasi */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden p-4 md:p-6">
                <div className="relative">
                    {/* Tombol Previous */}
                    {slides.length > 1 && (
                        <button
                            onClick={prevSlide}
                            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition"
                        >
                            <FaChevronLeft size={20} />
                        </button>
                    )}

                    {/* Gambar */}
                    <div className="overflow-auto max-h-[600px] w-full flex justify-center">
                        <img
                            src={getImageUrl(currentSlide.image_url)}
                            alt={currentSlide.judul || `Struktur Organisasi ${activeSlide + 1}`}
                            className="object-contain transition-transform duration-300"
                            style={{
                                transform: `scale(${zoom})`,
                                transformOrigin: 'top center'
                            }}
                        />
                    </div>

                    {/* Tombol Next */}
                    {slides.length > 1 && (
                        <button
                            onClick={nextSlide}
                            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition"
                        >
                            <FaChevronRight size={20} />
                        </button>
                    )}

                    {/* Indikator slide */}
                    {slides.length > 1 && (
                        <>
                            <div className="flex justify-center gap-2 mt-4">
                                {slides.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            setActiveSlide(index);
                                            setZoom(1);
                                        }}
                                        className={`w-3 h-3 rounded-full transition ${activeSlide === index
                                                ? 'bg-[#2E7D32]'
                                                : 'bg-gray-300 hover:bg-gray-400'
                                            }`}
                                    />
                                ))}
                            </div>
                            <p className="text-center text-sm text-gray-500 mt-2">
                                {currentSlide.label || `Slide ${activeSlide + 1}`} dari {slides.length}
                            </p>
                        </>
                    )}
                </div>
            </div>

            {currentSlide.sumber && (
                <div className="mt-4 text-center text-xs text-gray-400 border-t border-gray-200 pt-4">
                    Sumber: {currentSlide.sumber}
                </div>
            )}
        </div>
    );
};

export default StrukturOrganisasi;